import React, { useState, useRef, useEffect } from 'react';
import '../styles/Projects.scss';
import profileImage from '../images/profile.jpeg'; // Using this as a fallback

// Import project images
import tymexImage from '../images/projects/tymex.png';
import sevenCirclesImage from '../images/projects/sevencircles.png';
import acbImage from '../images/projects/acb.png';
import boschImage from '../images/projects/bosch.png';
import fptImage from '../images/projects/fpt.png';

const Projects = () => {
  const [activeCompany, setActiveCompany] = useState('all');
  const projectsRef = useRef(null);

  // Project data with categories
  const projects = [
    {
      id: 1,
      title: 'Personal Loan Application',
      image: tymexImage,
      description: 'Banking web application for personal loans that manages the entire process from verification to servicing.',
      tech: ['AWS', 'Python', 'SpringBoot', 'Microservices'],
      company: 'TymeX',
      category: 'finance',
      links: {
        github: '',  // Add links if available
        live: ''     // Add links if available
      }
    },
    {
      id: 2,
      title: 'Fin-ally',
      image: sevenCirclesImage,
      description: 'Financial planning application that helps users manage their investments and track their financial goals.',
      tech: ['Java', 'SpringBoot', 'ReactJS', 'MySQL', 'Elasticsearch'],
      company: 'SEVEN CIRCLES',
      category: 'finance',
      links: {
        github: '',  // Add links if available
        live: ''     // Add links if available
      }
    },
    {
      id: 3,
      title: 'Prepa-inp',
      image: sevenCirclesImage,
      description: 'Educational platform designed to help students prepare for engineering entrance examinations.',
      tech: ['Java', 'SpringBoot', 'ReactJS', 'MySQL', 'Docker'],
      company: 'SEVEN CIRCLES',
      category: 'education',
      links: {
        github: '',  // Add links if available
        live: ''     // Add links if available
      }
    },
    {
      id: 4,
      title: 'Reveal',
      image: sevenCirclesImage,
      description: 'Data processing application that visualizes and analyzes complex datasets for business insights.',
      tech: ['Java', 'Guice', 'ReactJS', 'Elasticsearch', 'Docker'],
      company: 'SEVEN CIRCLES',
      category: 'data',
      links: {
        github: '',  // Add links if available
        live: ''     // Add links if available
      }
    },
    {
      id: 5,
      title: 'Mobile Banking',
      image: acbImage,
      description: 'Mobile banking platform that enables customers to perform banking operations from their smartphones.',
      tech: ['Java', 'SpringBoot', 'Postgres', 'Kubernetes', 'Microservices'],
      company: 'Asia Commercial Bank',
      category: 'finance',
      links: {
        github: '',  // Add links if available
        live: ''     // Add links if available
      }
    },
    {
      id: 6,
      title: 'Virtual Debit Card',
      image: acbImage,
      description: 'System for creating and managing virtual debit cards for secure online transactions.',
      tech: ['Java', 'SpringBoot', 'Docker', 'AWS S3'],
      company: 'Asia Commercial Bank',
      category: 'finance',
      links: {
        github: '',  // Add links if available
        live: ''     // Add links if available
      }
    },
    {
      id: 7,
      title: 'ePin',
      image: acbImage,
      description: 'Electronic PIN management system for secure card transactions and authentication.',
      tech: ['Java', 'SpringBoot', 'Postgres', 'Docker'],
      company: 'Asia Commercial Bank',
      category: 'security',
      links: {
        github: '',  // Add links if available
        live: ''     // Add links if available
      }
    },
    {
      id: 8,
      title: 'Sunlife Insurance',
      image: acbImage,
      description: 'Insurance platform integration for banking customers to access and purchase insurance products.',
      tech: ['Java', 'Microservices', 'Docker', 'Kubernetes'],
      company: 'Asia Commercial Bank',
      category: 'finance',
      links: {
        github: '',  // Add links if available
        live: ''     // Add links if available
      }
    },
    {
      id: 9,
      title: 'WorkOn',
      image: boschImage,
      description: 'Web application for generating business case forms for global teams, streamlining the approval process.',
      tech: ['JavaScript', 'Bosch Internal Framework', 'HTML', 'CSS'],
      company: 'Robert Bosch Engineering',
      category: 'enterprise',
      links: {
        github: '',  // Add links if available
        live: ''     // Add links if available
      }
    },
    {
      id: 10,
      title: 'CES-Blue',
      image: fptImage,
      description: 'Dashboard and web application for US energy companies to monitor and control energy consumption.',
      tech: ['Spring Boot', 'AngularJS', 'SQL', 'RESTful APIs'],
      company: 'FPT Software',
      category: 'energy',
      links: {
        github: '',  // Add links if available
        live: ''     // Add links if available
      }
    },
    {
      id: 11,
      title: 'Personal Portfolio',
      image: profileImage,
      description: 'My personal portfolio website showcasing my projects, skills, and professional experience.',
      tech: ['React', 'Tailwind CSS', 'SCSS', 'JavaScript'],
      company: 'Personal Project',
      category: 'personal',
      links: {
        github: 'https://github.com/AntonyLe23/my-portfolio',
        live: 'https://le-thinh-phat-portfolio.netlify.app/'  // Add your deployed portfolio link when available
      }
    }
  ];

  // Setup animation for projects section
  useEffect(() => {
    // Set up animations for project cards
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe section elements
    if (projectsRef.current) {
      const animatedElements = projectsRef.current.querySelectorAll('.company-section, .project-card');
      animatedElements.forEach((el) => observer.observe(el));
    }

    return () => {
      if (projectsRef.current) {
        const animatedElements = projectsRef.current.querySelectorAll('.company-section, .project-card');
        animatedElements.forEach((el) => observer.unobserve(el));
      }
    };
  }, []);

  // Function to render a project card
  const renderProjectCard = (project) => (
    <div className="project-card" key={project.id}>
      <div className="project-image">
        <img src={project.image} alt={project.title} />
        <div className="project-overlay">
          <h3>{project.title}</h3>
        </div>
      </div>
      <div className="project-content">
        <p>{project.description}</p>
        <div className="project-tech">
          {project.tech.map((tech, techIndex) => (
            <span key={techIndex}>{tech}</span>
          ))}
        </div>
        <p className="project-company"><em>{project.company}</em></p>
        <div className="project-links">
          {project.links.github && (
            <a href={project.links.github} target="_blank" rel="noopener noreferrer">
              GitHub
            </a>
          )}
          {project.links.live && (
            <a href={project.links.live} target="_blank" rel="noopener noreferrer">
              Live Demo
            </a>
          )}
        </div>
      </div>
    </div>
  );

  // Group projects by company
  const groupProjectsByCompany = () => {
    const groupedProjects = {};
    
    // Group all projects by company
    projects.forEach(project => {
      const { company } = project;
      if (!groupedProjects[company]) {
        groupedProjects[company] = [];
      }
      groupedProjects[company].push(project);
    });
    
    return groupedProjects;
  };
  
  // Get unique company names for filter buttons
  const companyNames = ['all', ...new Set(projects.map(project => project.company))];
  
  // Get projects based on the current filter
  const filteredProjects = activeCompany === 'all' 
    ? groupProjectsByCompany() 
    : { [activeCompany]: projects.filter(project => project.company === activeCompany) };
  
  return (
    <section className="projects-section" ref={projectsRef}>
      <h2 className="section-title">Projects</h2>
      
      {/* Company filter buttons */}
      <div className="filter-buttons">
        {companyNames.map(company => (
          <button 
            key={company} 
            className={activeCompany === company ? 'active' : ''}
            onClick={() => setActiveCompany(company)}
          >
            {company === 'all' ? 'All Companies' : company}
          </button>
        ))}
      </div>

      {/* Display projects grouped by company */}
      {Object.keys(filteredProjects).length > 0 ? (
        Object.entries(filteredProjects).map(([company, companyProjects]) => (
          <div key={company} className="company-section">
            <h3 className="company-name">{company}</h3>
            <div className="projects-grid">
              {companyProjects.map(project => renderProjectCard(project))}
            </div>
          </div>
        ))
      ) : (
        <p>No projects found for this company.</p>
      )}
    </section>
  );
};

export default Projects;
